import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Generic hook for paginated debounced option loading (DDL/autocomplete).
 *
 * fetchPage: ({ typed, page }) => Promise<{ items: Array<{value,label}>, totalPages: number }>
 */
export default function usePagedOptions({ fetchPage, debounceMs = 300 }) {
  const [typed, setTyped] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const debounceRef = useRef();

  const debounced = useCallback(
    (fn) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fn, debounceMs);
    },
    [debounceMs]
  );

  const reset = useCallback(() => {
    setOptions([]);
    setPage(0);
    setTotalPages(1);
  }, []);

  const onMenuOpen = useCallback(() => setPage(1), []);

  const onInputChange = useCallback(
    (val) => {
      setTyped(val || "");
      if (!val) {
        reset();
      } else {
        setOptions([]);
        setPage(1);
      }
    },
    [reset]
  );

  const onMenuScrollToBottom = useCallback(() => {
    if (!isLoading && page < totalPages) setPage((p) => p + 1);
  }, [isLoading, page, totalPages]);

  useEffect(() => {
    if (page <= 0) return;
    setIsLoading(true);
    debounced(async () => {
      try {
        const res = await fetchPage({ typed, page });
        const items = Array.isArray(res?.items) ? res.items : [];
        const tp = Number(res?.totalPages || 1);
        setOptions((prev) => [...prev, ...items]);
        setTotalPages(tp);
      } finally {
        setIsLoading(false);
      }
    });
  }, [debounced, fetchPage, page, typed]);

  return useMemo(
    () => ({
      typed,
      page,
      totalPages,
      options,
      isLoading,
      onMenuOpen,
      onInputChange,
      onMenuScrollToBottom,
      reset,
      setOptions,
    }),
    [typed, page, totalPages, options, isLoading, onMenuOpen, onInputChange, onMenuScrollToBottom, reset]
  );
}
